"use client";

import {
  isTelemetryEventName,
  type TelemetryEventName,
  type TelemetryEventPayload,
} from "@/lib/analytics/events";

type TrackBody<TEventName extends TelemetryEventName> = {
  eventName: TEventName;
  payload: TelemetryEventPayload<TEventName>;
  appSurface?: "client" | "coach" | "admin" | "public";
  sessionId?: string;
  platform?: string;
};

type AnyTrackBody = TrackBody<TelemetryEventName>;

const BATCHABLE_EVENT_NAMES = new Set<TelemetryEventName>([
  "screen_view",
  "top_level_tab_opened",
  "subtab_opened",
  "navigation_timing",
]);
const TELEMETRY_BATCH_QUEUE_KEY = "telemetryEventBatchQueue";
const TELEMETRY_BATCH_MAX_SIZE = 12;
const TELEMETRY_BATCH_MAX_AGE_MS = 90_000;

let flushTimerId: number | null = null;
let pagehideListenerBound = false;
let flushInFlight = false;

function getTelemetryQueueStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  }
}

function readQueuedTelemetryEvents(): AnyTrackBody[] {
  const storage = getTelemetryQueueStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(TELEMETRY_BATCH_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueuedTelemetryEvents(events: AnyTrackBody[]) {
  const storage = getTelemetryQueueStorage();
  if (!storage) return;
  try {
    if (events.length === 0) {
      storage.removeItem(TELEMETRY_BATCH_QUEUE_KEY);
      return;
    }
    storage.setItem(TELEMETRY_BATCH_QUEUE_KEY, JSON.stringify(events));
  } catch {
    // Best-effort only.
  }
}

function clearScheduledFlush() {
  if (flushTimerId == null || typeof window === "undefined") return;
  window.clearTimeout(flushTimerId);
  flushTimerId = null;
}

async function postTelemetryEventsNow(events: AnyTrackBody[]) {
  if (events.length === 0) return;
  const response = await fetch("/api/telemetry", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(events.length === 1 ? events[0] : { events }),
    keepalive: true,
    cache: "no-store",
  });
  if (response.ok) {
    const result = await response.json().catch(() => null);
    if (
      result &&
      typeof result === "object" &&
      "droppedInvalidCount" in result &&
      typeof result.droppedInvalidCount === "number" &&
      result.droppedInvalidCount > 0
    ) {
      console.warn("[Telemetry] Server dropped invalid telemetry items", {
        droppedInvalidCount: result.droppedInvalidCount,
        attemptedEventCount: events.length,
        eventNames: events.map((event) => event.eventName),
      });
    }
  }
  return response.ok;
}

function flushQueuedTelemetryEventsWithBeacon() {
  if (typeof window === "undefined") return;
  const events = readQueuedTelemetryEvents();
  if (events.length === 0) return;
  clearScheduledFlush();
  try {
    const payload = JSON.stringify(events.length === 1 ? events[0] : { events });
    const blob = new Blob([payload], { type: "application/json" });
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const accepted = navigator.sendBeacon("/api/telemetry", blob);
      if (accepted) {
        writeQueuedTelemetryEvents([]);
      }
      return;
    }
  } catch {
    // Fall through to no-op.
  }
}

async function flushQueuedTelemetryEvents() {
  if (flushInFlight) return;
  const events = readQueuedTelemetryEvents();
  if (events.length === 0) return;
  flushInFlight = true;
  clearScheduledFlush();
  try {
    const delivered = await postTelemetryEventsNow(events);
    if (delivered) {
      const queuedNow = readQueuedTelemetryEvents();
      writeQueuedTelemetryEvents(queuedNow.slice(events.length));
    }
  } catch {
    // Keep queued events for a later retry.
  } finally {
    flushInFlight = false;
    if (readQueuedTelemetryEvents().length > 0) {
      scheduleTelemetryFlush();
    }
  }
}

function ensureTelemetryBatchLifecycle() {
  if (typeof window === "undefined" || pagehideListenerBound) return;
  pagehideListenerBound = true;
  window.addEventListener("pagehide", flushQueuedTelemetryEventsWithBeacon);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushQueuedTelemetryEventsWithBeacon();
    }
  });
}

function scheduleTelemetryFlush() {
  if (typeof window === "undefined" || flushTimerId != null) return;
  flushTimerId = window.setTimeout(() => {
    flushTimerId = null;
    void flushQueuedTelemetryEvents();
  }, TELEMETRY_BATCH_MAX_AGE_MS);
}

export function getTelemetrySessionId() {
  if (typeof window === "undefined") return "server";
  const key = "telemetrySessionId";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const next =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  sessionStorage.setItem(key, next);
  return next;
}

export function getTelemetryPlatform() {
  if (typeof window === "undefined") return "server";
  const platform = document.documentElement.dataset.capacitorPlatform;
  if (platform) return `native-${platform}`;
  return "web";
}

export async function postTelemetryEvent<TEventName extends TelemetryEventName>(
  body: TrackBody<TEventName>
) {
  if (!isTelemetryEventName(body.eventName)) return;

  if (BATCHABLE_EVENT_NAMES.has(body.eventName)) {
    ensureTelemetryBatchLifecycle();
    const queued = [...readQueuedTelemetryEvents(), body as AnyTrackBody];
    writeQueuedTelemetryEvents(queued);
    if (queued.length >= TELEMETRY_BATCH_MAX_SIZE) {
      void flushQueuedTelemetryEvents();
    } else {
      scheduleTelemetryFlush();
    }
    return;
  }

  try {
    await postTelemetryEventsNow([body as AnyTrackBody]);
  } catch {
    // Best-effort only.
  }
}
